// react
import React from 'react';

// material ui icons
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';

// material ui
import {
  Avatar,
  Card,
  CardActionArea,
  CardContent,
  CardHeader,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

// shared components
import InfoTooltipIcon from '@/components/ui/icon/InfoTooltipIcon';

/**
 * Reusable summary card for the Initiatives tab.
 *
 * Shares the same layout/structure across cards (icon avatar + title +
 * info tooltip in the header, caption/subtitle/value in the content) while
 * letting each caller supply its own icon, text and value content.
 *
 * Props:
 * - icon: React node (e.g., <FolderRoundedIcon />)
 * - iconColor: string or theme function (avatar icon color)
 * - iconBgColor: string or theme function (avatar background color)
 * - tooltip: string (info tooltip text shown in the header action)
 * - title: string or node (CardHeader title)
 * - caption: string or node (small caption text above the subtitle)
 * - subtitle: string or node (secondary title text)
 * - value: node (main value content, e.g. a Typography or a row of them)
 * - loading: boolean (shows a skeleton instead of the value)
 * - dimmed: boolean (fades the card content, e.g. when another card is filtered)
 * - onClick: function (click handler for the whole card)
 */
const InitiativeCard = ({
  icon,
  iconColor = '#c2dd00',
  iconBgColor = alpha('#c2cc00', 0.1),
  tooltip,
  title,
  caption,
  subtitle,
  value,
  loading = false,
  dimmed = false,
  onClick,
}) => {
  return (
    <Card sx={{ height: '100%' }}>
      <CardActionArea onClick={onClick} sx={{ height: '100%' }}>
        <CardHeader
          title={title}
          avatar={
            <Avatar
              variant="rounded"
              sx={{
                bgcolor: iconBgColor,
                borderRadius: 3,
              }}
            >
              {icon &&
                React.cloneElement(icon, {
                  sx: { color: iconColor },
                })}
            </Avatar>
          }
          action={
            tooltip && (
              <InfoTooltipIcon title={tooltip} placement="top" arrow>
                <InfoRoundedIcon
                  sx={{
                    color: (theme) =>
                      theme.palette.mode === 'light' ? 'lightgray' : 'gray',
                    ml: 1,
                  }}
                  fontSize="small"
                />
              </InfoTooltipIcon>
            )
          }
          sx={{
            opacity: dimmed ? 0.2 : 1,
            transition: 'opacity 0.3s ease-in-out',
          }}
          slotProps={{
            title: {
              variant: 'subtitle1',
              fontWeight: 600,
            }
          }}
        />

        <CardContent>
          <Typography
            variant="caption"
            gutterBottom
            sx={{
              opacity: dimmed ? 0.2 : 1,
              transition: 'opacity 0.3s ease-in-out',
            }}
          >
            {caption}
          </Typography>

          <Typography
            variant="subtitle1"
            fontWeight={600}
            color="textSecondary"
            gutterBottom
            sx={{
              opacity: dimmed ? 0.2 : 1,
              transition: 'opacity 0.3s ease-in-out',
            }}
          >
            {subtitle}
          </Typography>

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            {loading ? (
              <Skeleton variant="text" width="100%" height={40} />
            ) : (
              <Stack
                direction="row"
                alignItems="baseline"
                sx={{
                  opacity: dimmed ? 0.2 : 1,
                  transition: 'opacity 0.3s ease-in-out',
                }}
              >
                {value}
              </Stack>
            )}
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

export default InitiativeCard;
